import { createRouter, createWebHistory } from 'vue-router'
import AuthenticationView from '@/views/AuthenticationView.vue'
import { useAuthStore } from '../stores/authStore'
import authService from '../services/authService'
import { isChunkLoadError, reloadForStaleChunk } from '../utils/chunkReload'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'auth',
      component: AuthenticationView,
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      component: () => import('../views/VerifyEmailView.vue'),
    },
    {
      // Demo mode entry point — main.ts detects this and seeds a fake session.
      path: '/demo',
      redirect: '/dashboard/usage',
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('../views/HomeView.vue'),
      children: [
        {
          path: '',
          redirect: '/dashboard/usage',
        },
        {
          path: 'users',
          name: 'users',
          component: () => import('../views/CompanyUsersView.vue'),
        },
        {
          path: 'info',
          redirect: '/dashboard/settings',
        },
        {
          path: 'usage',
          name: 'usage',
          component: () => import('../views/UsageView.vue'),
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import('../views/SettingsView.vue'),
        },
        {
          path: 'subscription',
          name: 'subscription',
          component: () => import('../views/SubscriptionView.vue'),
        },
        // Superadmin routes
        {
          path: 'superadmin/users',
          name: 'superadmin-users',
          component: () => import('../views/superadmin/UsersView.vue'),
          beforeEnter: () => {
            const authStore = useAuthStore()
            if (!(authStore.user?.is_admin ?? false)) {
              return { name: 'dashboard' }
            }
          },
        },
        {
          path: 'superadmin/reports',
          name: 'superadmin-reports',
          component: () => import('../views/superadmin/ReportsView.vue'),
          beforeEnter: () => {
            const authStore = useAuthStore()
            if (!(authStore.user?.is_admin ?? false)) {
              return { name: 'dashboard' }
            }
          },
        },
        {
          path: 'superadmin/recent-profiles',
          name: 'superadmin-recent-profiles',
          component: () => import('../views/superadmin/RecentProfilesView.vue'),
          beforeEnter: () => {
            const authStore = useAuthStore()
            if (!(authStore.user?.is_admin ?? false)) {
              return { name: 'dashboard' }
            }
          },
        },
        {
          path: 'superadmin/super-settings',
          name: 'superadmin-super-settings',
          component: () => import('../views/superadmin/SuperSettingsView.vue'),
          beforeEnter: () => {
            const authStore = useAuthStore()
            if (!(authStore.user?.is_admin ?? false)) {
              return { name: 'dashboard' }
            }
          },
        },
      ],
    },
  ],
})

router.beforeEach(async (to, from) => {
  const authStore = useAuthStore()

  if (
    authStore.token &&
    (to.name === 'auth' || to.name === 'verify-email' || from.name === 'auth')
  ) {
    try {
      console.log('Refreshing user data after auth...')
      const user = await authService.getCurrentUser()
      authStore.$patch({ user })
    } catch (error) {
      console.error('Failed to load user:', error)
      authStore.clearAuth()
      return { name: 'auth' }
    }
  }

  // If we have a token but no user, wait for user data
  if (authStore.token && !authStore.user) {
    try {
      console.log('Loading user data...')
      const user = await authService.getCurrentUser()
      authStore.$patch({ user })
    } catch (error) {
      console.error('Failed to load user:', error)
      authStore.clearAuth()
      return { name: 'auth' }
    }
  }

  console.log('Router Guard Debug:')
  console.log('Current route:', to.name)
  console.log('User signed in:', authStore.isUserSignedIn)
  console.log('User:', authStore.user)
  console.log('Email confirmed:', authStore.isUserEmailConfirmed)
  console.log('Confirmed at:', authStore.user?.confirmed_at)

  if (!authStore.isUserSignedIn && to.name !== 'auth') {
    console.log('→ Redirecting to auth (not signed in)')
    return { name: 'auth' }
  } else if (
    authStore.isUserSignedIn &&
    !authStore.isUserEmailConfirmed &&
    to.name !== 'verify-email'
  ) {
    console.log('→ Redirecting to verify-email (email not confirmed)')
    return { name: 'verify-email' }
  } else if (
    authStore.isUserSignedIn &&
    authStore.isUserEmailConfirmed &&
    (to.name === 'auth' || to.name === 'verify-email')
  ) {
    console.log('→ Redirecting to dashboard (email confirmed)')
    return { name: 'dashboard' }
  }
  console.log('→ Proceeding to requested route')
})

router.onError((error, to) => {
  if (isChunkLoadError(error)) {
    reloadForStaleChunk(error, to.fullPath)
  }
})

export default router
